import { ArrowUpRight, Plus } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import type { WorkspaceSummary } from '../../../lib/workspaces';
import { useWorkspaceRows } from '../sidebar/hooks/use-workspace-rows';
import { TextButton, Typography } from '../ui';
import { HorizontalScroller } from '../HorizontalScroller';
import { WorkspaceBadge } from '../WorkspaceBadge';
import styles from './WorkspacesPanel.module.css';

// The user's workspaces under the home band — ONE row, always (Figma 10324:115045). It used to have a
// second shape (a titled column of cards, for two or three workspaces); there is one shape now, and
// the row is dropped entirely below two workspaces, where there is nothing to choose between.
// Mirrors native's `HomeWorkspacesList` — except for the ≥2 gate, which is web-only for now (a tracked
// divergence, see orion-web/CLAUDE.md).
//
// The row NEVER wraps: past the width it scrolls, with the same chevrons + edge fade the jobs strip
// uses. Wrapping turned the row into a two-line block that read as a grid of tiles rather than one
// strip of chips, and pushed the dashboard down a line for no gain.

const PAGE = 'home';

/** Figma 12486:86308 — the "spaces nav" badge is 20 square in the row. */
const BADGE = 20;

/** Figma 10324:115046 — 24 between items. */
const GAP = 24;

export function WorkspacesPanel({
  workspaces,
  filledCreateGlyph = false,
  ligatureLabels = false,
}: {
  workspaces: WorkspaceSummary[];
  /**
   * Draw the create affordance's `+` on a FILLED white tile rather than in an outlined box
   * (Figma `19106:277187`) — the same 20×20 at radius 6 the sibling Space badges wear, so the row's
   * three leading marks are one shape.
   *
   * Opt-in, defaulting to the outlined box this panel has always drawn, because the CATCH-UP home
   * renders this same component and is not behind the Home 2 flag — a change here would reach every
   * account. The Overview home passes it; nothing else does.
   */
  filledCreateGlyph?: boolean;
  /**
   * Turn on the labels' discretionary ligatures (Figma `19106:277192`) — the ONE line of that frame's
   * type panel that `TextButton size="medium"` does not already draw.
   *
   * Opt-in for the same reason as `filledCreateGlyph`: the catch-up home renders this component and is
   * behind no flag.
   */
  ligatureLabels?: boolean;
}) {
  const { t } = useTranslation();
  // Creating a space is an ASK — spaces are agent-created, so the affordance opens a Maestro
  // conversation (the same handoff the sidebar's "Create a workspace" row uses), not a form.
  const { create } = useWorkspaceRows();

  return (
    <section aria-labelledby="workspaces-heading" className={styles.row}>
      {/* The items read as their own labels and the design carries no visible title, so the heading is
          for screen readers only — hence no `sym-section-title` (#7244): there is nothing to style.
          Restore that class alongside the visual style if a visible title ever comes back. */}
      <Typography variant="h3" color="strong" component="h2" id="workspaces-heading" className={styles.heading}>
        {t('home.workspaces.title')}
      </Typography>
      <HorizontalScroller
        gap={GAP}
        ariaPrev={t('home.workspaces.scrollPrev')}
        ariaNext={t('home.workspaces.scrollNext')}
        // `overflow-x` makes the browser compute `overflow-y: auto` too, which would clip the Ghost
        // button's focus ring at the row's top/bottom edge. Give it vertical room, pulled back by the
        // same amount so the divider below still sits where it did.
        rowStyle={{ paddingBlock: 6, marginBlock: -6 }}
      >
        {workspaces.map((ws) => (
          <WorkspaceItem key={ws.id} ws={ws} labelClassName={ligatureLabels ? styles.ligatureLabel : undefined} />
        ))}
        {/* Create a new space — the last item in the strip. The leading glyph sits in the badge slot like
            the other items: an outlined box + plus by default (Figma 13528:106923), or a filled white tile
            when the caller asks (`filledCreateGlyph`, Figma 19106:277187). Opens the Maestro handoff with the
            right context (a "new" conversation prefilled with the create prompt), as the rail row. */}
        <div className={styles.item}>
          <span className={filledCreateGlyph ? styles.createGlyphFilled : styles.createGlyph} aria-hidden>
            <Plus size={14} />
          </span>
          <TextButton
            size="medium"
            href={create.route}
            linkAs={create.path}
            onClick={create.onClick}
            className={ligatureLabels ? styles.ligatureLabel : undefined}
          >
            {create.label}
          </TextButton>
        </div>
      </HorizontalScroller>
      {/* A fixed sibling, not inside the scroller — the hairline spans the content width and does not
          scroll with the row (same as native's strip). */}
      <div className={styles.divider} />
    </section>
  );
}

function WorkspaceItem({ ws, labelClassName }: { ws: WorkspaceSummary; labelClassName?: string }) {
  const { logPageClickBi } = useGenericBi();
  const encoded = encodeURIComponent(ws.slug);
  return (
    // Figma 12486:86308: the badge sits beside a DS Text button (283:4833) carrying the name + ↗.
    // TextButton has no box and no fill — its hover is an ink-colour shift, not a pill — so the
    // typography and hover both come from the DS component, not hand-rolled here.
    <div className={styles.item}>
      <WorkspaceBadge ws={ws} size={BADGE} />
      <TextButton
        size="medium"
        href={`/workspace/${encoded}`}
        className={labelClassName}
        endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
        onClick={() =>
          // The workspace opened is the entity acted on. `ws.id` (not the slug) is what the native
          // apps send, so the same open-workspace funnel joins across the three surfaces.
          logPageClickBi(
            { page_name: PAGE, button_name: 'open_workspace' },
            { componentName: 'open-workspace', entity: { id: ws.id, type: ENTITY_TYPE.workspace } },
          )
        }
      >
        {ws.name}
      </TextButton>
    </div>
  );
}
